/**
 * `Open`/`Error` → `InReview`: the transition that makes a count postable.
 *
 * **Built at D6, and it had to be**, though the phase doc never gave it a screen
 * number: `PostSession`'s first guard is `Status = InReview`, so without this the
 * confirm S6 exists for could never be reached from a count a manager had just
 * finished entering. It is also the "one way forward" S7 offers a failed post.
 *
 * Two states, deliberately not one gesture:
 *
 * **From `Open`** this is the ordinary end of entry — one tap, no dialog. Nothing is
 * destroyed and nothing posts; lines stay editable at `InReview`, so it is a stage,
 * not a lock.
 *
 * **From `Error` it destroys evidence, and so it asks.** `ValidateApiTransition`
 * **blanks `ErrorText`** on the way through, and that text is Business Central's own
 * account of why the post failed, naming the line and the item. It is the reason S5
 * renders the failure above every gate and the reason this control sits underneath
 * it: a manager must read the message before being offered the thing that erases it.
 *
 * Not rendered at all where the server would refuse it — no button that produces a
 * 403 or a 409, which is the same rule `CancelSession` follows.
 */
import { useState } from "react";

import { ApiError } from "../api/client";
import { useSubmitForReview as useSubmitMutation } from "../api/queries";
import type { CountSession } from "../api/types";

/** The two statuses the AL accepts into `InReview`. Mirrors `SUBMITTABLE_STATUSES`. */
const SUBMITTABLE = ["Open", "Error"];

export function SubmitForReview({
  session,
  areaId,
  mine,
}: {
  session: CountSession;
  areaId: string;
  mine: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = useSubmitMutation(areaId, session.session_id);

  if (!mine || !SUBMITTABLE.includes(session.status)) return null;

  const destroysText = session.status === "Error" && Boolean(session.error_text);

  const send = async () => {
    setError(null);
    try {
      await submit.mutateAsync();
      setConfirming(false);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail
          : `Could not submit this count for review: ${(err as Error).message}`,
      );
    }
  };

  return (
    <div className="card" data-testid="submit-panel">
      <h3>{session.status === "Error" ? "Try this count again" : "Finished counting?"}</h3>
      {!confirming ? (
        <>
          <p className="small">
            {session.status === "Error"
              ? "Submitting it for review again is the only way back from a failed post. Nothing posts until you confirm it separately."
              : "Marking the count as reviewed is what makes it postable. It posts nothing on its own — that is a separate, explicit confirmation."}
          </p>
          <button
            type="button"
            data-testid="submit-for-review"
            disabled={submit.isPending}
            onClick={() => (destroysText ? setConfirming(true) : void send())}
          >
            {submit.isPending ? "Submitting…" : "Submit for review"}
          </button>
          {error && (
            <p className="error-text small" data-testid="submit-error">
              {error}
            </p>
          )}
        </>
      ) : (
        <div className="confirm" data-testid="submit-confirm">
          <p>
            <strong>Read Business Central's message first — this erases it.</strong>
          </p>
          <p className="small">
            Submitting a failed count for review again clears the error text above. It
            is the only way back, and the message does not come back with it. Copy it
            somewhere if anyone else needs to see it.
          </p>
          {error && (
            <p className="error-text small" data-testid="submit-error">
              {error}
            </p>
          )}
          <div className="confirm__buttons">
            <button
              type="button"
              disabled={submit.isPending}
              data-testid="submit-confirm-yes"
              onClick={() => void send()}
            >
              {submit.isPending ? "Submitting…" : "I've read it — submit for review"}
            </button>
            <button type="button" className="ghost" onClick={() => setConfirming(false)}>
              Not yet
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
